import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import WinLossHistory from './WinLossHistory';
import './Profile.css';

function Profile({ onLogout }) {

  const { user, userProfile, refreshProfile, signOut } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(0);

  useEffect(() => {
    fetchWallet();
  }, [user]);

  const fetchWallet = async () => {
    if (!user) return;
    
    try {
      // Try to get existing wallet (should be created by trigger with $10 bonus)
      let { data, error } = await supabase
        .from('user_wallet')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw error;
      }

      if (!data) {
        // Wallet doesn't exist yet - trigger might still be processing
        // Wait a bit for trigger to complete (race condition handling)
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Try fetching again after waiting
        const { data: retryData, error: retryError } = await supabase
          .from('user_wallet')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (retryError && retryError.code === 'PGRST116') {
          // Trigger didn't create wallet, create manually as fallback
          // Give $10 bonus even in fallback case
          const { data: newWallet, error: createError } = await supabase
            .from('user_wallet')
            .insert([
              {
                user_id: user.id,
                balance: 10.00, // Signup bonus
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              }
            ])
            .select()
            .single();

          if (createError) throw createError;
          data = newWallet;
        } else if (retryData) {
          // Trigger created wallet, use it
          data = retryData;
        } else if (retryError) {
          throw retryError;
        }
      }

      setWallet(data);
      setBalance(data.balance || 0);
    } catch (err) {
      console.error('Error fetching wallet:', err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate container padding based on header - 10px from main menu
  const [containerPaddingTop, setContainerPaddingTop] = useState(100);
  
  useEffect(() => {
    const updatePadding = () => {
      const header = document.querySelector('.user-header');
      if (header) {
        const headerRect = header.getBoundingClientRect();
        setContainerPaddingTop(headerRect.bottom + 10); // 10px from main menu
      }
    };

    updatePadding();
    window.addEventListener('resize', updatePadding);
    
    const header = document.querySelector('.user-header');
    let resizeObserver;
    if (header && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(updatePadding);
      resizeObserver.observe(header);
    }

    return () => {
      window.removeEventListener('resize', updatePadding);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="profile-container" style={{ paddingTop: `${containerPaddingTop}px` }}>
        <div className="profile-loading">Loading wallet...</div>
      </div>
    );
  }

  return (
    <div className="profile-container" style={{ paddingTop: `${containerPaddingTop}px` }}>
      {/* Profile Settings Card */}
      <div className="wallet-card-main" style={{ marginBottom: '20px' }}>
        <div className="wallet-header-main">
          <div className="wallet-icon-main">👤</div>
          <div style={{ flex: 1 }}>
            <h1 className="wallet-title">{userProfile?.nickname || user?.email?.split('@')[0] || 'User'}</h1>
            <p className="wallet-subtitle">Balance: ${balance.toLocaleString()}</p>
            <div className="profile-logout-container">
              <button
                className="logout-btn"
                onClick={async () => {
                  await signOut();
                  if (onLogout) onLogout();
                }}
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Betting History Section */}
        {user && (
          <div style={{ marginTop: '32px', paddingTop: '32px', borderTop: '1px solid rgba(59, 130, 246, 0.2)' }}>
            <h2 style={{ 
              color: '#ffffff', 
              fontSize: '20px', 
              fontWeight: 600, 
              marginBottom: '16px' 
            }}>
              Betting History
            </h2>
            <WinLossHistory user={user} />
          </div>
        )}
      </div>
    </div>
  );
}

export default Profile;
